import type {
  InputConfig,
  InputDescription,
} from "@smartsuite/automation-engine-api";
import { ConditionsLogicOperator } from "@smartsuite/automation-engine-api";
import type { TriggerMerger } from ".";
import { getDefaultConditionsGroup } from ".";
import type { TriggerCandidate } from "../../../../types/candidate";
import { TriggerLabel } from "../../../../utils/constants";
import { pickValueConfigString } from "../../../../utils/inputs";

export const mergeInputDescriptionList =
  (
    descriptionList: InputDescription[],
    conditionListCleanup: boolean,
  ): TriggerMerger =>
  (trigger: TriggerCandidate) => ({
    ...trigger,
    inputs: descriptionList.map((description) => {
      let input = trigger?.inputs.find(
        (i) => i.inputId === description.inputId,
      );

      if (
        input?.value !== undefined &&
        description.editor?.editor.oneofKind === "select"
      ) {
        if (!isValueInOptions(description, input)) {
          input = {
            inputId: description.inputId,
            valueDescription: description.value,
            value: {
              values: [
                {
                  value: {
                    oneofKind: "string",
                    string: description.editor.editor.select.default ?? "",
                  },
                },
              ],
            },
          };
        }
      }

      return (
        input ?? {
          inputId: description.inputId,
          valueDescription: description.value,
          value: {
            values: [
              {
                value: {
                  oneofKind: "string",
                  string: "",
                },
              },
            ],
          },
        }
      );
    }),
    conditions: conditionListCleanup
      ? // Add empty condition as default for Matches Condition trigger
        trigger.triggerReference?.triggerId === TriggerLabel.matchesCondition
        ? {
            operator: ConditionsLogicOperator.OPERATOR_AND,
            group: [getDefaultConditionsGroup(trigger)],
          }
        : {
            operator: ConditionsLogicOperator.OPERATOR_AND,
            group: [],
          }
      : trigger.conditions,
  });

export const mergeInputById =
  (nextInput: InputConfig): TriggerMerger =>
  (trigger: TriggerCandidate) => {
    const inputs = [...trigger.inputs];
    const index = inputs.findIndex(
      (input) => input.inputId === nextInput.inputId,
    );
    if (index === -1) {
      inputs.push(nextInput);
    } else {
      inputs.splice(index, 1, nextInput);
    }
    return { ...trigger, inputs };
  };

const isValueInOptions = (
  description: InputDescription,
  input: InputConfig,
): boolean => {
  if (
    input?.value !== undefined &&
    description.editor?.editor.oneofKind === "select"
  ) {
    const options = description.editor.editor.select.options;
    const option = options.find((o) => {
      if (input.value !== undefined) {
        return o.value === pickValueConfigString(input.value);
      }
      return false;
    });
    return option !== undefined;
  }
  return true;
};
